import { JSX, useLayoutEffect } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import axios from 'axios';
import { parseISO } from 'date-fns';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

interface Props {
  children: JSX.Element;
}

class DateHandler {
  // https://stackoverflow.com/questions/3143070/javascript-regex-iso-datetime
  // NB: This is not a full match regex as it quickly becomes complex with various iso-date formats
  static isoDateFormat =
    /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+)|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d)|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d)/;

  static isIsoDateString = (value: any): boolean => {
    return (
      value &&
      typeof value === 'string' &&
      value.length < 40 &&
      DateHandler.isoDateFormat.test(value)
    );
  };

  static convertIsoStringsToJsDates = (body: any) => {
    if (body === null || body === undefined || typeof body !== 'object') return body;

    for (const key of Object.keys(body)) {
      const value = body[key];
      if (DateHandler.isIsoDateString(value)) body[key] = parseISO(value);
      else if (typeof value === 'object') DateHandler.convertIsoStringsToJsDates(value);
    }
  };
}

// Ensures that a valid access token is attached to all outgoing requests
const EnsureAuthToken = (props: Props) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0]);

  // Layout effect so the interceptor is installed before any child useEffect
  // fires a request. Registering during render instead would add a new
  // interceptor on every render, and each one triggers its own token call.
  useLayoutEffect(() => {
    /* eslint-disable no-param-reassign */
    const requestInterceptorId = axios.interceptors.request.use(async (config) => {
      if (!account) {
        throw Error('No active account! Verify a user has been signed in.');
      }

      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });

        const bearer = `Bearer ${response.accessToken}`;
        config.headers.Authorization = bearer;
      } catch (error) {
        // Silent renewal runs in a hidden iframe. When that cannot complete
        // (expired session, blocked third-party cookies) only an interactive
        // sign-in recovers it.
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ ...loginRequest, account });
        }
        throw error;
      }

      return config;
    });
    /* eslint-enable no-param-reassign */

    // convert all json iso-strings to js dates
    const responseInterceptorId = axios.interceptors.response.use((originalResponse) => {
      DateHandler.convertIsoStringsToJsDates(originalResponse.data);
      return originalResponse;
    });

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, [instance, account]);

  return <>{props.children}</>;
};

export default EnsureAuthToken;
